import type {
  IAccessRequestWaiting,
  DecisionActRecordType,
  IDecisionAnswerRecord,
  IDecisionQuestionShown,
  IQuestion,
  IQuestionOption,
  QuestionSelectionModeType,
} from '@featit/shared';
import type { IQuestionDefinitionProblem, RevisionRequestKindType } from '@featit/shared/questions';
import { QUESTION_DEFINITION_PROBLEM_CODES } from '@featit/shared/question-codes';
import { prefetchAtBoot, takeBootPrefetch } from './bootPrefetch';
import type { IActorRef, IUserAgent } from './WorkItemApi';
import { gql, type GraphqlRequesterType } from './GraphqlClient';
import { workAreaApis } from '@/data-sources/WorkAreaApiRegistry';
import { GraphqlRequestError } from './graphqlRequestError';
import { trackDecisionWrite } from './decisionWrites';

/**
 * The decision reads and writes the web app makes (F-0178, `docs/concept-decisions-and-questions.md`
 * §3, ADR-097). A decision is a `D-` item with one question; the rules for who may answer and what an
 * answer may contain are `@featit/shared`'s (`questions.ts`), so this file only carries the requests.
 */

/** The shallow item a decision links to: itself, what it blocks, what it supersedes or is superseded by. */
export interface IDecisionItemRef {
  id: string;
  /** What it blocks may be in another work area (I-0423). */
  workAreaId?: string;
  key: string;
  title: string;
  state?: { name: string; color?: string | null; statusKey?: string | null } | null;
}

export interface IDecision {
  /**
   * `content` is the decision's context text, which a signed answer covers (F-0489, §2.2). Only the
   * single-decision read (`get`) selects it; on a list's decision it is absent.
   */
  item: IDecisionItemRef & {
    content?: string | null;
    /**
     * I-2134 (AC-11.190): who holds the decision's item, which is who answers it (ADR-097), with the
     * agent's key its presence is read by, so a row draws the agent's state. Absent from an older read.
     */
    responsible?:
      | (Pick<IActorRef, 'actorType' | 'agentId'> & {
          agent?: Pick<IUserAgent, 'id' | 'key' | 'displayName'> | null;
        })
      | null;
  };
  /** The shared question shape, so `isQuestionResponsible` and `questionAnswerProblems` apply as-is. */
  question: IQuestion;
  /** "Open", "Refused", "Answered", "Superseded" or "Withdrawn" — the server's `decisionStatusLabel`. */
  statusLabel: string;
  supersedes: IDecisionItemRef | null;
  supersededBy: IDecisionItemRef | null;
  blocks: IDecisionItemRef[];
  /** What it blocks in work areas this reader cannot read: counted, never named (I-0423). */
  hiddenBlockCount?: number;
  adr: string | null;
  /**
   * While it waits on its responsible, since when, from the server (F-0522, §11c TF-25/TF-27): the
   * handover, the last reopen or the taken-back answer. Null once it does not wait.
   */
  waitingSince?: string | null;
  /** Why it is late — a missed service level or a past target date — or null (TF-29). */
  overdueReason?: string | null;
  /**
   * I-1154: the work area's sync revision this decision was read at. It reflects every item event
   * at or below it, so the panel drops this item's events up to it as already shown.
   */
  revision?: number | null;
}

export interface IAnswerDecisionInput {
  optionKeys: string[];
  otherText?: string | null;
  note?: string | null;
  /** F-0489: the record this device signed for this answer, its signature, and what was shown. */
  signed?: ISignedDecisionAnswer | null;
}

export interface ISignedDecisionAnswer {
  record: IDecisionAnswerRecord;
  signature: string;
  shown: IDecisionQuestionShown;
  /** F-0499 (AC-109): the passkey's confirmation of this record, where presence is required. */
  presence?: unknown;
}

/** F-0501 (Slice 10b): a take-back or a move as this device signed it. */
export interface ISignedDecisionAct {
  record: DecisionActRecordType;
  signature: string;
  /** Slice 10c (AC-126): the passkey's assertion over the act's presence challenge, where required. */
  presence?: unknown;
}

export interface IClaimDecisionInput {
  displace?: boolean;
  expectedResponsible?: string;
  /** F-0501 (AC-118): the move as this device signed it. */
  signed?: ISignedDecisionAct | null;
}

/**
 * `FileDecisionInput` as the web form sends it: the context as its three parts, which the server
 * renders into the description with `renderDecisionContent`, so the Options section is never typed
 * twice. Exactly one of `responsibleUserId` / `responsibleAgentId` is present.
 */
export interface IFileDecisionInput {
  workAreaId: string;
  title: string;
  background: string;
  options: IQuestionOption[];
  selectionMode: QuestionSelectionModeType;
  allowOther: boolean;
  recommendation: string;
  recommendedOptionKey: string;
  blocksKeys: string[];
  blocksText: string;
  responsibleActorType: 'user' | 'agent';
  responsibleUserId?: string;
  responsibleAgentId?: string;
}

/**
 * `ReviseDecisionInput` (I-0484): the question and its shape, in the parts a filing takes. The
 * responsible, blocks relations and ADR have their own paths and are not here.
 */
export interface IReviseDecisionInput {
  title?: string;
  background: string;
  options: IQuestionOption[];
  selectionMode: QuestionSelectionModeType;
  recommendation: string;
  recommendedOptionKey: string;
  blocksText: string;
}

/**
 * The server's `decision_invalid` refusal, as its list of problems — the same sentences
 * `questionDefinitionProblems` writes, plus the server's own. `null` for any other failure.
 */
export function decisionInvalidProblemsOf(error: unknown): string[] | null {
  return decisionRefusalProblemsOf(error)?.map((entry) => entry.message) ?? null;
}

/**
 * I-2194: the same refusal, each problem with its code where it is a question rule's: the server
 * sends `questionProblems` (code, option, values, sentence) beside its English sentences, and a
 * sentence that matches one carries it, so the form words it in the reader's language.
 */
export function decisionRefusalProblemsOf(
  error: unknown,
): Array<{ message: string; problem: IQuestionDefinitionProblem | null }> | null {
  if (!(error instanceof GraphqlRequestError)) return null;
  const body = error.extensions.originalError;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const { reason, problems, questionProblems } = body as {
    reason?: unknown;
    problems?: unknown;
    questionProblems?: unknown;
  };
  if (reason !== 'decision_invalid' || !Array.isArray(problems)) return null;
  const coded = (Array.isArray(questionProblems) ? questionProblems : []).filter(
    (entry): entry is IQuestionDefinitionProblem =>
      Boolean(entry) &&
      typeof entry === 'object' &&
      (QUESTION_DEFINITION_PROBLEM_CODES as readonly unknown[]).includes(
        (entry as { code?: unknown }).code,
      ) &&
      typeof (entry as { sentence?: unknown }).sentence === 'string' &&
      Boolean((entry as { values?: unknown }).values) &&
      typeof (entry as { values?: unknown }).values === 'object',
  );
  const sentences = problems.filter((entry): entry is string => typeof entry === 'string');
  if (sentences.length === 0) return null;
  return sentences.map((message) => ({
    message,
    problem: coded.find((entry) => entry.sentence === message) ?? null,
  }));
}

const ITEM_REF_FIELDS = `
  id
  workAreaId
  key
  title
  state { name color statusKey }
`;

const ACTOR_FIELDS = `
  actorType
  userId
  agentId
  displayName
`;

/**
 * I-1061: every reference across a decision's own non-Milkdown text — its options' labels and
 * consequences, the answer's free text and note, the refusal's and withdrawal's notes — resolved
 * once (`Question.contentReferences`, `backend/src/decisions/question-content-references.resolver.ts`),
 * so `DecisionPanel`'s `LinkifiedText` calls can turn a key written in any of those fields into a
 * link without a request per field. Same shape as `WorkItemApi`'s `CONTENT_REFERENCE_FIELDS`.
 */
const QUESTION_CONTENT_REFERENCE_FIELDS = `
  contentReferences {
    token
    kind
    item { id key title state itemTypeKey workAreaId }
    matches { heading file excerpt }
    unresolvedNote
  }
`;

/** I-2134 (AC-11.190): the decision item's responsible, with the agent's key its presence is read by. */
const ITEM_RESPONSIBLE_FIELDS = `
  responsible { actorType agentId agent { id key displayName } }
`;

/** F-0807: the question as it stood, the before and the after of a revision. */
const REVISION_SNAPSHOT_FIELDS = `
  prompt
  options { key label consequence }
  recommendedOptionKey
`;

/** F-0807: the responsible's request for a revision, with the question as it stood. */
const REVISION_REQUEST_FIELDS = `
  note
  kinds
  requestedAt
  requestedBy { ${ACTOR_FIELDS} }
  before { ${REVISION_SNAPSHOT_FIELDS} }
`;

export const DECISION_FIELDS = `
  item { ${ITEM_REF_FIELDS} ${ITEM_RESPONSIBLE_FIELDS} }
  statusLabel
  adr
  supersedes { ${ITEM_REF_FIELDS} }
  supersededBy { ${ITEM_REF_FIELDS} }
  blocks { ${ITEM_REF_FIELDS} }
  hiddenBlockCount
  waitingSince
  overdueReason
  revision
  question {
    id
    workAreaId
    prompt
    selectionMode
    allowOther
    commentId
    decisionItemId
    createdAt
    options { key label consequence }
    askedBy { ${ACTOR_FIELDS} }
    responsible { ${ACTOR_FIELDS} }
    answer {
      optionKeys
      otherText
      note
      answeredAt
      answeredBy { ${ACTOR_FIELDS} }
      signed { bindingStrength client keyThumbprint }
    }
    retractedAnswer {
      optionKeys
      otherText
      note
      answeredAt
      answeredBy { ${ACTOR_FIELDS} }
      retractedAt
      retractedBy { ${ACTOR_FIELDS} }
    }
    answerHistory {
      optionKeys
      otherText
      note
      answeredAt
      answeredBy { ${ACTOR_FIELDS} }
      takenBack { at by { ${ACTOR_FIELDS} } }
      signed { bindingStrength client keyThumbprint }
    }
    refusal {
      note
      refusedAt
      refusedBy { ${ACTOR_FIELDS} }
    }
    withdrawal {
      note
      withdrawnAt
      withdrawnBy { ${ACTOR_FIELDS} }
    }
    revisionRequest { ${REVISION_REQUEST_FIELDS} }
    revisedAfterRequest {
      revisedAt
      request { ${REVISION_REQUEST_FIELDS} }
      after { ${REVISION_SNAPSHOT_FIELDS} }
    }
    ${QUESTION_CONTENT_REFERENCE_FIELDS}
  }
`;

/**
 * F-0489 (§2.2): the panel's single-decision read also selects the decision's context text, which a
 * signed answer covers. Lists stay lean: only this read carries it, and the signing path builds what
 * was shown only from it (`signedAnswer.ts`).
 */
export const DECISION_DETAIL_FIELDS = `${DECISION_FIELDS}
  item { content }`;

const WAITING_EVERYWHERE = 'decisions-waiting-for-me-everywhere';

async function readWaitingForMeEverywhere(): Promise<IDecision[]> {
  const data = await gql<{ decisionsWaitingForMe: IDecision[] }>(
    `query DecisionsWaitingForMeEverywhere($allWorkAreas: Boolean) {
      decisionsWaitingForMe(allWorkAreas: $allWorkAreas) { ${DECISION_FIELDS} }
    }`,
    { allWorkAreas: true },
  );
  return data.decisionsWaitingForMe;
}

/**
 * The decisions API over one connection's GraphQL (I-1120): `decisionApiFor(workAreaId)` reaches the deployment that holds the work area; `decisionApi` is the main
 * deployment's.
 */
export function createDecisionApi(request: GraphqlRequesterType = gql) {
  return {
    async get(workAreaId: string, key: string): Promise<IDecision> {
      const data = await request<{ decision: IDecision }>(
        `query Decision($workAreaId: ID!, $key: String!) {
        decision(workAreaId: $workAreaId, key: $key) { ${DECISION_DETAIL_FIELDS} }
      }`,
        { workAreaId, key },
      );
      return data.decision;
    },

    async waitingForMe(workAreaId: string): Promise<IDecision[]> {
      const data = await request<{ decisionsWaitingForMe: IDecision[] }>(
        `query DecisionsWaitingForMe($workAreaId: ID!) {
        decisionsWaitingForMe(workAreaId: $workAreaId) { ${DECISION_FIELDS} }
      }`,
        { workAreaId },
      );
      return data.decisionsWaitingForMe;
    },

    /**
     * The same list across every work area this reader may read — the All work areas scope (I-0560).
     *
     * ⛔ **One request, not one per work area.** The server answers across the areas holding
     * `work_item.read`, so an area the reader may not open contributes nothing and is never named.
     * `item.workAreaId` on each decision is what the grouping reads.
     *
     * ⛔ **The scope is said** — `allWorkAreas: true` — rather than implied by leaving the work area
     * out (I-0560, AC-8). An omitted work area is refused by the server, so a bug here that dropped
     * the argument would fail loudly instead of quietly returning more than this call meant to ask.
     */
    waitingForMeEverywhere(): Promise<IDecision[]> {
      // T-0559: the launch tab's first read may already be under way since boot.
      return takeBootPrefetch(WAITING_EVERYWHERE, readWaitingForMeEverywhere);
    },

    /** T-0559: starts the All work areas list at boot, for the Decisions tab the app launches into. */
    prefetchWaitingForMeEverywhere(): void {
      prefetchAtBoot(WAITING_EVERYWHERE, readWaitingForMeEverywhere);
    },

    /**
     * The same list across every work area in one namespace's whole subtree (F-0383, §10.9 NV-8) —
     * `waitingForMeEverywhere`'s own rule, narrowed: one request, the server intersects the subtree
     * with what this reader may read, so a work area in it the reader cannot open contributes nothing.
     */
    async waitingForMeInNamespace(namespaceId: string): Promise<IDecision[]> {
      const data = await request<{ decisionsWaitingForMe: IDecision[] }>(
        `query DecisionsWaitingForMeInNamespace($namespaceId: ID) {
        decisionsWaitingForMe(namespaceId: $namespaceId) { ${DECISION_FIELDS} }
      }`,
        { namespaceId },
      );
      return data.decisionsWaitingForMe;
    },

    /**
     * F-0713 (AC-SV18): the access requests waiting for this reader's answer, in a scope the
     * decisions take: one work area, every one (`allWorkAreas: true`, said, as I-0560 asks), or one
     * namespace. Who may answer is the server's; this only asks.
     */
    async accessRequestsWaiting(
      scope: { workAreaId: string } | { allWorkAreas: true } | { namespaceId: string },
    ): Promise<IAccessRequestWaiting[]> {
      const data = await request<{ accessRequestsWaitingForMe: IAccessRequestWaiting[] }>(
        `query AccessRequestsWaitingForMe($workAreaId: ID, $allWorkAreas: Boolean, $namespaceId: ID) {
        accessRequestsWaitingForMe(workAreaId: $workAreaId, allWorkAreas: $allWorkAreas, namespaceId: $namespaceId) {
          ref agentName ownerName workAreaId workAreaName workAreaPath missing requesterWords
          lastRequestedAt closesAt mayDecide
        }
      }`,
        scope,
      );
      return data.accessRequestsWaitingForMe;
    },

    async answer(id: string, input: IAnswerDecisionInput): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ answerDecision: IDecision }>(
          `mutation AnswerDecision($id: ID!, $input: AnswerDecisionInput!) {
          answerDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          { id, input },
        );
        return data.answerDecision;
      });
    },

    async file(input: IFileDecisionInput): Promise<IDecision> {
      const data = await request<{ fileDecision: IDecision }>(
        `mutation FileDecision($input: FileDecisionInput!) {
        fileDecision(input: $input) { ${DECISION_FIELDS} }
      }`,
        { input },
      );
      return data.fileDecision;
    },

    /**
     * File a decision that replaces an open one (DC-6, I-0476).
     *
     * A changed mind never edits an answer (decision 41), so this is the only way a settled question is
     * reopened: the successor is filed with a `supersedes` relation, the old one moves to `cancelled`,
     * and each names the other. `id` is the **superseded** decision's item id.
     */
    async supersede(id: string, input: IFileDecisionInput): Promise<IDecision> {
      const data = await request<{ supersedeDecision: IDecision }>(
        `mutation SupersedeDecision($id: ID!, $input: FileDecisionInput!) {
        supersedeDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
      }`,
        { id, input },
      );
      return data.supersedeDecision;
    },

    /**
     * Decline to decide (F-0208): "I will not decide this", with the reason. Not an answer — the
     * decision stays open for whoever asked — and the reason is required by the server, which says so.
     */
    async refuse(id: string, note: string): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ refuseDecision: IDecision }>(
          `mutation RefuseDecision($id: ID!, $input: RefuseDecisionInput!) {
          refuseDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          { id, input: { note } },
        );
        return data.refuseDecision;
      });
    },

    /**
     * Ask for a revision (F-0807, `concept-decisions-and-questions.md` §5v): the responsible's feedback,
     * sent back to the asker instead of an answer. The feedback is required by the server, which says
     * so; the kinds sort it. The decision stays open and waits on its asker until it is revised.
     */
    async requestRevision(
      id: string,
      note: string,
      kinds: readonly RevisionRequestKindType[] = [],
    ): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ requestDecisionRevision: IDecision }>(
          `mutation RequestDecisionRevision($id: ID!, $input: RequestDecisionRevisionInput!) {
          requestDecisionRevision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          { id, input: { note, kinds: [...kinds] } },
        );
        return data.requestDecisionRevision;
      });
    },

    /**
     * The decisions sent back to the reader for a revision (F-0807, DR-2): the asker's side of
     * Decisions for me, in one work area, one namespace's subtree, or every work area they can read.
     */
    async waitingOnMeAsAsker(
      scope: { workAreaId?: string | null; namespaceId?: string | null } = {},
    ): Promise<IDecision[]> {
      const { workAreaId = null, namespaceId = null } = scope;
      const data = await request<{ decisionsWaitingOnAsker: IDecision[] }>(
        workAreaId
          ? `query DecisionsWaitingOnAsker($workAreaId: ID) {
        decisionsWaitingOnAsker(workAreaId: $workAreaId) { ${DECISION_FIELDS} }
      }`
          : namespaceId
            ? `query DecisionsWaitingOnAskerInNamespace($namespaceId: ID) {
        decisionsWaitingOnAsker(namespaceId: $namespaceId) { ${DECISION_FIELDS} }
      }`
            : `query DecisionsWaitingOnAskerEverywhere($allWorkAreas: Boolean) {
        decisionsWaitingOnAsker(allWorkAreas: $allWorkAreas) { ${DECISION_FIELDS} }
      }`,
        workAreaId ? { workAreaId } : namespaceId ? { namespaceId } : { allWorkAreas: true },
      );
      return data.decisionsWaitingOnAsker;
    },

    /**
     * Revise an unanswered decision in place (I-0484, ADR-102 §7): the same validation as filing, refused
     * by the server once the decision has an answer.
     */
    async revise(id: string, input: IReviseDecisionInput): Promise<IDecision> {
      const data = await request<{ reviseDecision: IDecision }>(
        `mutation ReviseDecision($id: ID!, $input: ReviseDecisionInput!) {
        reviseDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
      }`,
        { id, input },
      );
      return data.reviseDecision;
    },

    /**
     * Withdraw (I-0473, ADR-102): the asker's retraction, with its reason, by whoever may edit the
     * decision. The reason is required by the server; the panel checks it first only to save a trip.
     */
    async withdraw(id: string, note: string): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ withdrawDecision: IDecision }>(
          `mutation WithdrawDecision($id: ID!, $input: WithdrawDecisionInput!) {
          withdrawDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          { id, input: { note } },
        );
        return data.withdrawDecision;
      });
    },

    async claim(id: string, input: IClaimDecisionInput = {}): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ claimDecision: IDecision }>(
          `mutation ClaimDecision($id: ID!, $input: ClaimDecisionInput) {
          claimDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          { id, input },
        );
        return data.claimDecision;
      });
    },

    /**
     * Finish the closing of a decision whose answer stands but whose state stayed open (I-1275): the
     * move a failed answer left undone. Writes no answer. Only its responsible may.
     */
    async finishClosing(id: string): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ finishDecisionClosing: IDecision }>(
          `mutation FinishDecisionClosing($id: ID!) {
          finishDecisionClosing(id: $id) { ${DECISION_FIELDS} }
        }`,
          { id },
        );
        return data.finishDecisionClosing;
      });
    },

    /**
     * Take back an answer given by accident (I-0535), within the window — only its author may, whoever
     * is responsible now. Not an edit: the decision goes back to open with the text preserved as a draft.
     */
    async unanswer(id: string, signed: ISignedDecisionAct | null = null): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ unanswerDecision: IDecision }>(
          `mutation UnanswerDecision($id: ID!, $input: UnanswerDecisionInput) {
          unanswerDecision(id: $id, input: $input) { ${DECISION_FIELDS} }
        }`,
          // F-0501 (AC-118): the take-back as this device signed it, when it holds a key.
          { id, input: signed ? { signed } : null },
        );
        return data.unanswerDecision;
      });
    },

    /**
     * Undo a supersede filed by mistake (I-1031), while the successor is still unanswered: the
     * predecessor reopens with this question as a revision, and this decision is withdrawn, pointing
     * back. `id` is the **successor's** item id — the decision that supersedes another — not the
     * predecessor it undoes. The decision this returns is the reopened predecessor, a different item
     * from the one named by `id`.
     */
    async unsupersede(id: string): Promise<IDecision> {
      return trackDecisionWrite(id, async () => {
        const data = await request<{ unsupersedeDecision: IDecision }>(
          `mutation UnsupersedeDecision($id: ID!) {
          unsupersedeDecision(id: $id) { ${DECISION_FIELDS} }
        }`,
          { id },
        );
        return data.unsupersedeDecision;
      });
    },
  };
}

export type DecisionApiType = ReturnType<typeof createDecisionApi>;

/** The main deployment's API. A work area's own is `decisionApiFor(workAreaId)`. */
export const decisionApi = createDecisionApi();

/** This API for the connection that holds `workAreaId` (I-1120). */
export function decisionApiFor(workAreaId: string | null | undefined): DecisionApiType {
  return workAreaApis.api(
    workAreaId,
    'decisionApi',
    (clients) => createDecisionApi(clients.gql),
    decisionApi,
  );
}
